/*
 * variant-availability-fix.js
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 *
 * Shopify's Liquid `value.available` (on product.options_with_values) means
 * "this option value appears in at least one available variant" — it is NOT
 * aware of what the shopper has selected for the OTHER options.
 *
 * On a single-option product that distinction doesn't matter, so the theme's
 * built-in "Show availability in selectors" works correctly there.
 *
 * On a two-option product (Size + Color) it breaks. Example, Called By Name
 * Pant Set: the only in-stock variant is M / CHARCOAL. Shopify therefore
 * renders Size M as data-available="true" no matter which colour is picked,
 * so M never gets crossed out under DARK GREEN or CREAM even though both of
 * those combinations are sold out.
 *
 * The theme's setDisabled() faithfully applies the `.is-disabled` class from
 * that data-available attribute, so the bug is in the data, not the JS.
 *
 * FIX: replace setDisabled() with a version that derives availability from the
 * embedded variant JSON (which carries per-variant `available`, so it IS
 * combination-aware) and cross-references the currently selected value of
 * every other option.
 *
 * Nothing else changes: we still toggle the same `.is-disabled` class the
 * theme's CSS already styles, and we still run at exactly the same points in
 * the lifecycle, because the theme calls setDisabled() itself after every
 * variant change (connectedCallback, renderVariantInfo, updateOther).
 *
 * INSTALL: load this AFTER component-variant-selects.js.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  /**
   * Pull the variant array out of whichever JSON blob this <variant-selects>
   * has. The main picker embeds a compact array (id/option1..3/options/
   * available); the sticky picker embeds the full Shopify variant objects.
   * Both expose option1..option3 and `available`, so one parser covers them.
   */
  function getVariants(root) {
    var scriptEl = null;

    var sectionId = root.dataset.section;
    if (sectionId) {
      scriptEl = document.getElementById('variant-data-' + sectionId);
    }
    if (!scriptEl || !root.contains(scriptEl)) {
      var own = root.querySelector('script[type="application/json"]');
      if (own) scriptEl = own;
    }
    if (!scriptEl) return null;

    try {
      var parsed = JSON.parse(scriptEl.textContent);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && Array.isArray(parsed.variants)) return parsed.variants;
    } catch (e) {
      console.warn('[variant-availability-fix] Could not parse variant JSON', e);
    }
    return null;
  }

  /** Normalise a variant into a positional array of option values. */
  function optionsOf(variant) {
    if (Array.isArray(variant.options)) return variant.options;
    return [variant.option1, variant.option2, variant.option3]
      .filter(function (v) { return v !== null && v !== undefined; });
  }

  /**
   * Zero-based option position for a fieldset, read from data-index="optionN".
   * Falls back to DOM order if the attribute is missing or malformed.
   */
  function positionOf(fieldset, domIndex) {
    var idx = fieldset.dataset ? fieldset.dataset.index : null;
    if (idx) {
      var m = idx.match(/option(\d+)/);
      if (m) return parseInt(m[1], 10) - 1;
    }
    return domIndex;
  }

  /** Currently chosen value in a fieldset (radio group or select). */
  function selectedValueOf(fieldset) {
    var checked = fieldset.querySelector('input[type="radio"]:checked');
    if (checked) return checked.value;
    var sel = fieldset.querySelector('select');
    if (sel && sel.value) return sel.value;
    return null;
  }

  function patchedSetDisabled() {
    // Respect the theme setting exactly as the original did.
    if (this.isDisabledFeature != 'true') return;

    var variants = getVariants(this);
    var fieldsets = Array.from(this.querySelectorAll('fieldset'));

    // No JSON to work from -> fall back to the server's data-available so we
    // never end up worse than the stock behaviour.
    if (!variants || !variants.length) {
      fieldsets.forEach(function (fieldset) {
        if (fieldset.querySelector('select')) {
          fieldset.querySelectorAll('option').forEach(function (opt) {
            opt.disabled = opt.dataset.available === 'false';
          });
        } else {
          fieldset.querySelectorAll('input').forEach(function (input) {
            input.classList.toggle('is-disabled', input.dataset.available === 'false');
          });
        }
      });
      return true;
    }

    // Snapshot what's currently selected for every option position.
    var selected = {};
    fieldsets.forEach(function (fieldset, i) {
      var pos = positionOf(fieldset, i);
      var val = selectedValueOf(fieldset);
      if (val !== null) selected[pos] = val;
    });

    /**
     * Is `candidate` at option position `pos` reachable given everything else
     * the shopper has already chosen? We ignore `pos` itself when matching so
     * that picking a new value for this option is always evaluated fairly.
     */
    function isAvailable(pos, candidate) {
      return variants.some(function (variant) {
        if (!variant.available) return false;

        var opts = optionsOf(variant);
        if (opts[pos] !== candidate) return false;

        for (var key in selected) {
          if (!Object.prototype.hasOwnProperty.call(selected, key)) continue;
          var otherPos = parseInt(key, 10);
          if (otherPos === pos) continue;
          if (opts[otherPos] !== selected[key]) return false;
        }
        return true;
      });
    }

    fieldsets.forEach(function (fieldset, i) {
      var pos = positionOf(fieldset, i);

      if (fieldset.querySelector('select')) {
        fieldset.querySelectorAll('option').forEach(function (opt) {
          if (!opt.value) return;
          var ok = isAvailable(pos, opt.value);
          opt.dataset.available = ok ? 'true' : 'false';
          opt.disabled = !ok;
        });
      } else {
        fieldset.querySelectorAll('input[type="radio"]').forEach(function (input) {
          var ok = isAvailable(pos, input.value);
          // Keep data-available in sync so anything else reading it agrees,
          // then drive the strikethrough with the same class the theme CSS
          // already targets: .variations input[type=radio].is-disabled + label
          input.dataset.available = ok ? 'true' : 'false';
          input.classList.toggle('is-disabled', !ok);
        });
      }
    });

    return true;
  }

  function applyPatch() {
    var VariantSelects = customElements.get('variant-selects');
    if (!VariantSelects) return false;

    if (VariantSelects.prototype.__availabilityPatched) return true;
    VariantSelects.prototype.setDisabled = patchedSetDisabled;
    VariantSelects.prototype.__availabilityPatched = true;

    // connectedCallback already ran with the old implementation, so re-run the
    // corrected one across every picker currently on the page (main + sticky).
    document.querySelectorAll('variant-selects, variant-radios').forEach(function (el) {
      if (typeof el.setDisabled !== 'function') return;
      // setDisabled reads this.fieldsets, populated by updateOptions().
      if (typeof el.updateOptions === 'function') {
        try { el.updateOptions(); } catch (e) { /* no-op */ }
      }
      try { el.setDisabled(); } catch (e) { /* no-op */ }
    });

    return true;
  }

  if (!applyPatch()) {
    // component-variant-selects.js hasn't defined the element yet — wait for it.
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('variant-selects').then(applyPatch);
    } else {
      document.addEventListener('DOMContentLoaded', applyPatch);
    }
  }
})();
